import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, serverTimestamp, doc, setDoc, getDocs, deleteDoc, addDoc } from "firebase/firestore";

export default function AdminVoices() {
    const [formData, setFormData] = useState({
        name: "",
        location: "",
        category: "",
        voiceHeader: "",
        text: ""
    });
    const [thumbnailImage, setThumbnailImage] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [voices, setVoices] = useState([]);
    const [userSubmittedVoices, setUserSubmittedVoices] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [activeTab, setActiveTab] = useState("add-new");

    const storage = getStorage();

    const fetchVoices = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "voices"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            setVoices(fetched);
        } catch (error) {
            console.error("Failed to fetch voices:", error);
        }
    };

    const fetchUserSubmittedVoices = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "user_submitted_voices"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            setUserSubmittedVoices(fetched);
        } catch (error) {
            console.error("Failed to fetch user submitted voices:", error);
        }
    };

    useEffect(() => {
        fetchVoices();
        fetchUserSubmittedVoices();
    }, []);

    const handleInput = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            setThumbnailImage(e.target.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            let thumbnailUrl = formData.thumbnailUrl || "";

            if (thumbnailImage) {
                const imgRef = ref(storage, `voicesimage/${Date.now()}_${thumbnailImage.name}`);
                await uploadBytes(imgRef, thumbnailImage);
                thumbnailUrl = await getDownloadURL(imgRef);
            }

            const numericId = editingId ? formData.id : Date.now();

            const voiceData = {
                id: numericId,
                name: formData.name,
                location: formData.location,
                category: formData.category,
                voiceHeader: formData.voiceHeader,
                text: formData.text,
                thumbnailUrl: thumbnailUrl,
                img: thumbnailUrl,
                updatedAt: serverTimestamp()
            };

            if (!editingId) {
                voiceData.createdAt = serverTimestamp();
                voiceData.approved = true; // Admin created voices are auto-approved
                await addDoc(collection(db, "voices"), voiceData);
                setMessage("Voice entry successfully added!");
            } else {
                await setDoc(doc(db, "voices", editingId), voiceData, { merge: true });
                setMessage("Voice entry successfully updated!");
            }

            cancelEdit();
            fetchVoices();
        } catch (error) {
            console.error("Error saving voice:", error);
            setMessage("Failed to save voice: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (voice) => {
        setEditingId(voice.dbId);
        setFormData({
            id: voice.id,
            name: voice.name || "",
            location: voice.location || "",
            category: voice.category || "",
            voiceHeader: voice.voiceHeader || "",
            text: voice.text || "",
            thumbnailUrl: voice.thumbnailUrl || ""
        });
        setThumbnailImage(null);
        setMessage("Editing voice entry for: " + voice.name);
    };

    const handleStatusUpdate = async (voice, newStatus) => {
        try {
            await setDoc(doc(db, "user_submitted_voices", voice.dbId), { status: newStatus }, { merge: true });
            setMessage(`Voice entry marked as ${newStatus}.`);
            fetchUserSubmittedVoices();
        } catch (error) {
            console.error("Error updating status:", error);
            setMessage("Failed to update status.");
        }
    };

    const handleDelete = async (voice) => {
        if (!window.confirm("Are you sure you want to delete this voice entry for '" + voice.name + "'?")) return;
        try {
            const collectionName = activeTab === 'add-new' ? "voices" : "user_submitted_voices";
            await deleteDoc(doc(db, collectionName, voice.dbId));
            setMessage("Voice entry deleted.");
            if (activeTab === 'add-new') {
                fetchVoices();
            } else {
                fetchUserSubmittedVoices();
            }
        } catch (error) {
            console.error("Error deleting:", error);
            setMessage("Failed to delete.");
        }
    };


    const cancelEdit = () => {
        setEditingId(null);
        setFormData({ name: "", location: "", category: "", voiceHeader: "", text: "", thumbnailUrl: "" });
        setThumbnailImage(null);
        setMessage("");
    };

    const styles = {
        container: { padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Syne', sans-serif" },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
        title: { fontSize: '2.5rem', fontWeight: '400', color: '#003049', margin: 0 },
        tabBar: { display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '1px solid #ddd', flexWrap: 'wrap' },
        tabBtn: { padding: '10px 15px', border: 'none', background: 'transparent', fontSize: '16px', fontWeight: '600', color: '#666', cursor: 'pointer', borderBottom: '2px solid transparent' },
        activeTabBtn: { padding: '10px 15px', border: 'none', background: 'transparent', fontSize: '16px', fontWeight: '600', color: '#003049', cursor: 'pointer', borderBottom: '2px solid #F07167' },
        mainFlex: { display: 'flex', gap: '40px', alignItems: 'flex-start', flexWrap: 'wrap' },
        formSection: { flex: 1, minWidth: '400px', background: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' },
        listSection: { flex: 1, minWidth: '400px', padding: '10px', height: '600px', overflowY: 'auto' },
        inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' },
        label: { fontSize: '14px', fontWeight: '600', color: '#003049' },
        input: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none' },
        textarea: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none', minHeight: '150px', resize: 'vertical' },
        submitBtn: { padding: '15px', background: '#F07167', color: 'white', borderRadius: '30px', cursor: 'pointer', border: 'none', fontWeight: 'bold', fontSize: '16px', width: '100%', marginTop: '10px', boxShadow: '0 4px 15px rgba(240,113,103,0.3)' },
        msg: { padding: '15px', background: '#eefbf3', color: '#155724', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c8e6c9', fontSize: '14px' },
        errorMsg: { padding: '15px', background: '#faeced', color: '#721c24', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f5c6cb', fontSize: '14px' },
        listTitle: { fontSize: '1.5rem', fontWeight: '400', color: '#003049', marginBottom: '20px' },
        card: { padding: '20px', border: 'none', borderRadius: '15px', marginBottom: '15px', background: 'white', display: 'flex', flexDirection: 'column', gap: '10px', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', position: 'relative' },
        cardAuthor: { margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#003049', paddingRight: '60px' },
        cardDetails: { margin: '0', fontSize: '13px', color: '#666', lineHeight: '1.6' },
        cardActions: { position: 'absolute', top: '20px', right: '20px', display: 'flex', flexDirection: 'row', gap: '10px' },
        actionBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#003049' },
        deleteBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#F07167' }
    };

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>{editingId ? "Edit Voice" : "Manage Voices"}</h1>
            </div>

            <div style={styles.tabBar}>
                <button 
                    style={activeTab === 'add-new' ? styles.activeTabBtn : styles.tabBtn}
                    onClick={() => { setActiveTab('add-new'); cancelEdit(); }}
                >
                    Add New Voice
                </button>
                <button 
                    style={activeTab === 'user-submitted' ? styles.activeTabBtn : styles.tabBtn}
                    onClick={() => { setActiveTab('user-submitted'); cancelEdit(); }}
                >
                    User Submitted Stories
                </button>
            </div>

            <div style={styles.mainFlex}>
                {activeTab === 'add-new' && (
                <div style={styles.formSection}>
                    {message && (
                        <div style={message.includes("Failed") ? styles.errorMsg : styles.msg}>
                            {message}
                        </div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Name</label>
                            <input name="name" value={formData.name} onChange={handleInput} style={styles.input} required placeholder="e.g. Luca Schneider" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Location</label>
                            <input name="location" value={formData.location} onChange={handleInput} style={styles.input} required placeholder="e.g. Cologne, Germany" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Category</label>
                            <input name="category" value={formData.category} onChange={handleInput} style={styles.input} placeholder="e.g. Resilience, Love, Hope" />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Summary</label>
                            <input name="voiceHeader" value={formData.voiceHeader} onChange={handleInput} style={styles.input} required placeholder="e.g. 2 sentences as just info about this voice." />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Image (Thumbnail)</label>
                            <input type="file" onChange={handleFileChange} style={styles.input} accept="image/*, .png, .jpg, .jpeg, .avif, .webp, .svg, .gif" />
                            {formData.thumbnailUrl && !thumbnailImage && (
                                <div style={{ marginTop: '10px' }}>
                                    <p style={{ fontSize: '12px', color: '#666', margin: '0 0 5px 0' }}>Current image uploaded:</p>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        <img src={formData.thumbnailUrl} alt="Current Voice Thumbnail" style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px' }} />
                                        <button 
                                            type="button" 
                                            onClick={() => setFormData({ ...formData, thumbnailUrl: "" })}
                                            style={{ padding: '5px 10px', background: '#F07167', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
                                        >
                                            Delete Image
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>Story</label>
                            <textarea name="text" value={formData.text} onChange={handleInput} style={styles.textarea} required placeholder="What is their story?" />
                        </div>

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button type="submit" style={styles.submitBtn} disabled={loading}>
                                {loading ? "Saving..." : (editingId ? "Update Voice" : "Add Voice")}
                            </button>
                            {editingId && (
                                <button type="button" onClick={cancelEdit} style={{ ...styles.submitBtn, background: '#eee', color: '#333', boxShadow: 'none' }}>
                                    Cancel
                                </button>
                            )}
                        </div>
                    </form>
                </div>
                )}

                <div className="no-scrollbar" style={{ ...styles.listSection, flex: activeTab === 'add-new' ? 1 : 'none', width: activeTab === 'add-new' ? 'auto' : '100%' }}>
                    <h2 style={{ fontSize: '1.5rem', fontWeight: '400', color: '#003049', marginBottom: '20px' }}>
                        {activeTab === 'add-new' ? `Published Voices (${voices.length})` : `User Submitted Stories (${userSubmittedVoices.length})`}
                    </h2>
                    {(activeTab === 'add-new' ? voices : userSubmittedVoices).length === 0 ? (
                        <p style={{ fontSize: '14px', color: '#777' }}>No voices found.</p>
                    ) : (
                        (activeTab === 'add-new' ? voices : userSubmittedVoices).map(voice => (
                            <div key={voice.dbId} style={styles.card}>
                                <div style={{ flex: 1 }}>
                                    <h3 style={styles.cardAuthor}>{voice.name}</h3>
                                    <p style={styles.cardDetails}>

                                        <strong>Location:</strong> {voice.location || 'Community'} <br />
                                        <strong>Category:</strong> {voice.category} <br />
                                        <strong>Summary:</strong> {voice.voiceHeader} <br />
                                        <br />
                                        <em>"{voice.text}"</em>
                                    </p>
                                    {voice.thumbnailUrl && (
                                        <img src={voice.thumbnailUrl} alt={voice.name} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '8px', marginTop: '10px' }} />
                                    )}
                                </div>
                                <div style={styles.cardActions}>
                                    {activeTab === 'add-new' && (
                                        <button onClick={() => handleEdit(voice)} style={styles.actionBtn} title="Edit Voice">
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M12 20h9"></path>
                                                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                            </svg>
                                        </button>
                                    )}
                                    {activeTab === 'user-submitted' && (
                                        <>
                                            {voice.status === 'approved' ? (
                                                <span style={{ color: 'green', display: 'flex', alignItems: 'center', padding: '8px' }} title="Approved">
                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                                    </svg>
                                                </span>
                                            ) : (
                                                <button onClick={() => handleStatusUpdate(voice, 'approved')} style={{ ...styles.actionBtn, color: 'green', fontWeight: 'bold' }} title="Approve">
                                                    Approve
                                                </button>
                                            )}
                                            <button onClick={() => handleStatusUpdate(voice, 'rejected')} style={{ ...styles.actionBtn, color: 'red', fontWeight: 'bold' }} title="Reject">
                                                Reject
                                            </button>
                                        </>
                                    )}
                                    <button onClick={() => handleDelete(voice)} style={styles.deleteBtn} title="Delete Voice">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3 6 5 6 21 6"></polyline>
                                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
